import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Edit2, Trash2, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MyReviewsPage = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentLang = i18n.language || 'es';

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        toast({
          title: t('reviews.error.authRequired'),
          description: t('reviews.error.authDescription'),
          variant: 'destructive'
        });
        return;
      }

      const response = await axios.get(`${API}/reviews/my-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Load reviews error:', error);
      toast({
        title: t('common.error'),
        description: t('reviews.error.load'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm(t('reviews.confirmDelete'))) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: t('common.success'),
        description: t('reviews.success.deleted')
      });
      
      loadReviews();
    } catch (error) {
      console.error('Delete review error:', error);
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('reviews.error.delete'),
        variant: 'destructive'
      });
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(currentLang.startsWith('es') ? 'es-CO' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: t('reviews.pendingReview') },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: t('reviews.published') },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: t('reviews.notApproved') }
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="text-center py-12 text-gray-500">{t('reviews.loading')}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('reviews.title')}</h2>
          <p className="text-gray-600 mt-1">{t('reviews.manage')}</p>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('reviews.noReviews')}</h3>
          <p className="text-gray-600">
            {t('reviews.noReviewsDescription')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className="overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">
                        {review.productName || t('accountOrders.product')}
                      </h3>
                      {getStatusBadge(review.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {renderStars(review.rating)}
                      <span>•</span>
                      <span>{formatDate(review.createdAt)}</span>
                      {review.verifiedPurchase && (
                        <>
                          <span>•</span>
                          <span className="text-green-600">✓ {t('reviews.verifiedPurchase')}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {review.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteReview(review.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{review.comment}</p>
                </div>

                {review.status === 'pending' && (
                  <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-sm text-yellow-800">
                    {t('reviews.pendingMessage')}
                  </div>
                )}

                {review.status === 'rejected' && (
                  <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-400 text-sm text-red-800">
                    {t('reviews.rejectedMessage')}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyReviewsPage;
